import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Search, User, RotateCcw, BookOpen, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { getModuleIcon } from '../../lib/moduleIcons';
import CourseProgressResetList from './CourseProgressResetList';

const BUILT_IN_SOURCES = [
  { key: 'tech', label: 'Tech Learning', resetKey: 'tech-learning', icon: BookOpen, iconClass: 'text-blue-500' },
  { key: 'finance', label: 'Finance Training', resetKey: 'finance-training', icon: DollarSign, iconClass: 'text-green-500' }
];

const toSource = (module) => ({
  key: `module-${module.slug}`,
  label: module.name,
  resetKey: module.slug,
  icon: getModuleIcon(module.icon),
  iconClass: 'text-orange-500'
});

/**
 * CourseRetryResetPanel - Admin panel to reset course assessment attempts
 * Covers Tech Learning, Finance Training and every active admin-created course module
 */
const CourseRetryResetPanel = ({ api }) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [sources, setSources] = useState(BUILT_IN_SOURCES);
  const [searchedUser, setSearchedUser] = useState(null);
  const [progressBySource, setProgressBySource] = useState({});
  const [activeTab, setActiveTab] = useState('tech');

  const loadSources = useCallback(async () => {
    try {
      const res = await api.get('/course-modules');
      const next = [...BUILT_IN_SOURCES, ...(res.data.modules || []).filter(m => m.is_active).map(toSource)];
      setSources(next);
      return next;
    } catch (err) {
      console.error('Failed to load course modules:', err);
      return BUILT_IN_SOURCES;
    }
  }, [api]);

  useEffect(() => {
    loadSources();
  }, [loadSources]);

  const fetchSourceProgress = async (source, userId) => {
    try {
      const res = await api.get(`/progress-reset/${source.resetKey}/users/${userId}/courses`);
      return res.data;
    } catch (err) {
      return { progress: [], progress_count: 0 };
    }
  };

  const searchUserProgress = async () => {
    const email = searchEmail.trim().toLowerCase();
    if (!email) {
      toast.error('Please enter a user email');
      return;
    }
    try {
      setSearching(true);
      setSearchedUser(null);
      setProgressBySource({});
      const usersRes = await api.get(`/users?email=${encodeURIComponent(email)}`);
      const users = usersRes.data.users || usersRes.data || [];
      const user = users.find(u => u.email?.toLowerCase() === email);
      if (!user) {
        toast.error('User not found');
        return;
      }
      const currentSources = await loadSources();
      const results = await Promise.all(currentSources.map(s => fetchSourceProgress(s, user.id)));
      setProgressBySource(Object.fromEntries(currentSources.map((s, i) => [s.key, results[i]])));
      setSearchedUser(user);
      if (!currentSources.some(s => s.key === activeTab)) setActiveTab('tech');
    } catch (err) {
      console.error('Failed to search user:', err);
      toast.error(err.response?.data?.detail || 'Failed to search user progress');
    } finally {
      setSearching(false);
    }
  };

  const refreshSource = async (source) => {
    if (!searchedUser) return;
    const refreshed = await fetchSourceProgress(source, searchedUser.id);
    setProgressBySource(prev => ({ ...prev, [source.key]: refreshed }));
  };

  return (
    <Card data-testid="course-retry-reset-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          Reset Course Assessment Attempts
        </CardTitle>
        <CardDescription>
          Search for a user to view their course builder progress (Tech, Finance and custom modules), then choose which module or sub-module assessments to reset
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1">
            <Label htmlFor="course-user-email" className="sr-only">User Email</Label>
            <Input
              id="course-user-email"
              placeholder="Enter user email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUserProgress()}
              data-testid="course-user-email-search"
            />
          </div>
          <Button onClick={searchUserProgress} disabled={searching} data-testid="search-course-user-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>

        {searchedUser && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 p-3 bg-muted rounded-lg">
              <User className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="font-medium" data-testid="course-reset-user-name">{searchedUser.name || 'Unknown'}</p>
                <p className="text-sm text-muted-foreground">{searchedUser.email}</p>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                {sources.map(s => (
                  <Badge key={s.key} variant="outline" data-testid={`course-reset-count-${s.key}`}>
                    {progressBySource[s.key]?.progress_count || 0} {s.label}
                  </Badge>
                ))}
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="flex flex-wrap h-auto w-full justify-start gap-1">
                {sources.map(s => {
                  const Icon = s.icon;
                  return (
                    <TabsTrigger key={s.key} value={s.key} className="flex items-center gap-2" data-testid={`course-reset-tab-${s.key}`}>
                      <Icon className="h-4 w-4" />
                      {s.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {sources.map(s => {
                const Icon = s.icon;
                return (
                  <TabsContent key={s.key} value={s.key} className="mt-4">
                    <CourseProgressResetList
                      api={api}
                      progressData={progressBySource[s.key]}
                      moduleType={s.key}
                      resetKey={s.resetKey}
                      user={searchedUser}
                      icon={<Icon className={`h-4 w-4 ${s.iconClass}`} />}
                      onReset={() => refreshSource(s)}
                    />
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CourseRetryResetPanel;
